import { useState, useEffect, Fragment } from "react";
import { toast } from "sonner";
import { CelebrationOverlay } from "./CelebrationOverlay";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Star, Trash2, Loader2, Plus, TrendingUp, Package, CheckCircle2, UserX,
  Clock, CalendarDays, History, Pencil, Camera, FilePlus2, ClipboardCheck, Activity, Wallet,
  Trophy, Sparkles, PartyPopper, Route,
} from "lucide-react";

const GRADE_CLS = {
  A: "bg-emerald-100 text-emerald-700", B: "bg-teal-100 text-teal-700",
  C: "bg-amber-100 text-amber-700", D: "bg-orange-100 text-orange-700",
  E: "bg-rose-100 text-rose-600", F: "bg-red-100 text-red-700",
};

const gradeFromScore = (score) => {
  if (typeof score !== "number") return null;
  const p = (score / 5) * 100;
  return p >= 90 ? "A" : p >= 80 ? "B" : p >= 70 ? "C" : p >= 60 ? "D" : p >= 50 ? "E" : "F";
};
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

const HOURS_MIN = 8;
const REJECT_MAX = 3;

const fmt = (n) => (n === null || n === undefined || n === "" ? "—" : n);
const pct = (n) => (typeof n === "number" ? Math.round(n * 1000) / 10 : null);
const hrs = (n) => (typeof n === "number" ? Number(n.toFixed(1)) : n);

const Stat = ({ icon: Icon, label, value, suffix = "", tone = "blue", onClick, hint }) => {
  const tones = {
    blue: "text-blue-600 bg-blue-50",
    green: "text-emerald-600 bg-emerald-50",
    amber: "text-amber-600 bg-amber-50",
    rose: "text-rose-600 bg-rose-50",
  };
  const clickable = !!onClick;
  const Comp = clickable ? "button" : "div";
  const danger = tone === "rose";
  return (
    <Comp {...(clickable ? { type: "button", onClick } : {})} data-testid={`perf-stat-${label}`}
      className={`w-full text-right rounded-2xl border bg-white p-4 flex items-center gap-3 ${danger ? "border-rose-200" : "border-slate-200"} ${clickable ? "cursor-pointer hover:border-blue-300 hover:shadow-sm transition" : ""}`}>
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${tones[tone]}`}><Icon className="h-5 w-5" /></span>
      <div className="min-w-0">
        <p className="text-[11px] text-slate-500 leading-tight">{label}</p>
        <p className={`font-heading text-lg font-bold font-mono-nums ${danger ? "text-rose-600" : "text-slate-800"}`}>{fmt(value)}{value !== null && value !== undefined && suffix}</p>
        {hint && <p className="text-[10px] text-slate-400 leading-tight mt-0.5">{hint}</p>}
      </div>
    </Comp>
  );
};

const DAY_COLS = [
  { key: "date", tkey: "date" },
  { key: "completed", tkey: "completed" },
  { key: "actual_hours", tkey: "actualHours" },
  { key: "acceptance_rate", tkey: "acceptance", rate: true },
  { key: "rejected", tkey: "rejected" },
  { key: "no_shows", tkey: "noShows" },
];

const DayHistoryModal = ({ open, onClose, days, highlight, minHours = HOURS_MIN, maxRejected = REJECT_MAX }) => {
  const { t, dir } = useLang();
  return (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="sm:max-w-xl font-body max-h-[88vh] overflow-y-auto p-4 sm:p-6" dir={dir} data-testid="perf-day-history">
      <DialogHeader className={dir === "rtl" ? "text-right" : "text-left"}><DialogTitle className="font-heading text-lg">{t("perf.history.title")}</DialogTitle>
      <DialogDescription className={`text-xs ${dir === "rtl" ? "text-right" : "text-left"}`}>{t("perf.history.desc")}</DialogDescription></DialogHeader>
      {(!days || days.length === 0) ? (
        <div className="py-8 text-center text-slate-400 text-sm">{t("perf.history.empty")}</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <div className="w-full grid text-xs text-center" style={{ gridTemplateColumns: "minmax(58px,auto) repeat(5, minmax(0,1fr))" }}>
            {DAY_COLS.map((c) => (
              <div key={c.key} className={`bg-slate-50 px-1 py-1.5 text-[10px] font-semibold whitespace-nowrap ${c.key === highlight ? "text-blue-600" : "text-slate-500"}`}>{t(`perf.day.${c.tkey}`)}</div>
            ))}
            {days.map((d, i) => {
              const hoursRed = typeof d.actual_hours === "number" && d.actual_hours < minHours;
              const rejRed = (d.rejected || 0) >= maxRejected;
              const base = `px-1 py-1.5 border-t border-slate-100 font-mono-nums flex items-center justify-center ${i === 0 ? "bg-blue-50/40" : ""}`;
              return (
                <Fragment key={d.date || i}>
                  <div data-testid={`perf-day-${d.date}`} className={`${base} text-slate-600 whitespace-nowrap`} dir="ltr">{d.date}</div>
                  <div className={`${base} ${highlight === "completed" ? "font-bold text-slate-800" : "text-slate-600"}`}>{d.completed ?? "—"}</div>
                  <div className={`${base} font-semibold ${hoursRed ? "text-rose-600" : highlight === "actual_hours" ? "text-slate-800" : "text-slate-600"}`}>{typeof d.actual_hours === "number" ? hrs(d.actual_hours) : (d.actual_hours ?? "—")}</div>
                  <div className={`${base} text-slate-600`}>{typeof d.acceptance_rate === "number" ? `${Math.round(d.acceptance_rate * 1000) / 10}` : "—"}</div>
                  <div className={`${base} font-semibold ${rejRed ? "text-rose-600" : highlight === "rejected" ? "text-slate-800" : "text-slate-600"}`}>{d.rejected ?? "—"}</div>
                  <div className={`${base} text-slate-600`}>{d.no_shows ?? "—"}</div>
                </Fragment>
              );
            })}
          </div>
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-1">{t("perf.history.legend", { min: minHours, max: maxRejected })}</p>
    </DialogContent>
  </Dialog>
  );
};

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
    ))}
  </span>
);

const TrendChart = ({ data, dataKey, name, color }) => (
  <div className="h-48 w-full" dir="ltr">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={36} />
        <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
        <Line type="monotone" dataKey={dataKey} name={name} stroke={color} strokeWidth={2.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

const EmptyBox = ({ icon: Icon, title, hint }) => (
  <div data-testid="perf-empty" className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-10 text-center">
    <Icon className="mx-auto h-9 w-9 text-blue-300" />
    <p className="mt-3 font-heading font-bold text-slate-600">{title}</p>
    {hint && <p className="text-sm text-slate-400 mt-1 px-6">{hint}</p>}
  </div>
);

export const PerformanceTab = ({ data, loading, editable, onDelete, showAdvances = true, celebrate = false, celebrateId = "", currentOnly = false }) => {
  const { t, lang } = useLang();
  const cur = t("portal.currency");
  const [hist, setHist] = useState(null);
  const [celebration, setCelebration] = useState(null);
  useEffect(() => {
    if (!celebrate) return;
    const r = data?.rider;
    if (!r || !r.matched) return;
    const tgt = Number(data?.thresholds?.order_target || 0);
    const done = Number(r.summary?.total_completed ?? 0);
    if (tgt <= 0) return;
    let type = null;
    if (done >= tgt) type = "party";
    else if (done >= tgt - 5) type = "thanks";
    if (!type) return;
    const monthKey = new Date().toISOString().slice(0, 7);
    const storeKey = `wz_celebrate_${celebrateId || "me"}_${monthKey}`;
    try {
      const seen = localStorage.getItem(storeKey);
      // party may override an earlier "thanks"; otherwise show each type once/month
      if (type === "party" ? seen === "party" : Boolean(seen)) return;
      localStorage.setItem(storeKey, type);
    } catch { /* storage unavailable */ }
    setCelebration(type);
  }, [celebrate, celebrateId, data]);
  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const rider = data?.rider || {};
  const curMonth = new Date().toISOString().slice(0, 7);
  const evals = (data?.evaluations || []).filter((ev) => !currentOnly || ev.month === curMonth);

  const advancesCard = (
    <div data-testid="perf-advances-card" className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
      <div>
        <p className="text-xs text-slate-500">{t("perf.advancesTotal")}</p>
        <p className="font-heading text-2xl font-bold text-rose-600 font-mono-nums mt-1">{Number(data?.advances_outstanding || 0).toLocaleString("en-US")} <span className="text-sm text-slate-400">{cur}</span></p>
      </div>
      <div className="h-11 w-11 rounded-xl bg-rose-50 flex items-center justify-center"><Wallet className="h-5 w-5 text-rose-500" /></div>
    </div>
  );

  const latest = rider.latest || {};
  const daily = rider.daily || [];
  const lh = latest.actual_hours;
  const lr = latest.rejected;
  const th = data?.thresholds || {};
  const HMIN = typeof th.min_hours === "number" ? th.min_hours : HOURS_MIN;
  const RMAX = typeof th.max_rejected === "number" ? th.max_rejected : REJECT_MAX;
  const openHist = (key) => setHist({ key });

  // Order target (تارجت): colour + motivational message based on total completed vs target.
  const target = typeof th.order_target === "number" ? th.order_target : 0;
  const totalCompleted = Number(rider.summary?.total_completed ?? 0);
  const reportDays = Number(rider.days || 0);
  const dailyTarget = target > 0 ? target / 30 : 0;
  const expectedTarget = target > 0 && reportDays > 0 ? Math.round(dailyTarget * reportDays) : 0;
  const gap = totalCompleted - expectedTarget;
  const TGT = {
    under: {
      grad: "from-rose-500 via-red-600 to-rose-700",
      badge: { ar: "لنكثّف الجهد 💪", en: "Let's push harder 💪", ur: "مزید محنت 💪" },
      msg: {
        ar: "أداؤك أقل من المطلوب لهذه الفترة — لا تقلق، كل طلب يقرّبك أكثر ونحن نثق بقدرتك على اللحاق! 💪🚀",
        en: "You're below the expected pace — every order counts, we believe you'll catch up! 💪🚀",
        ur: "آپ متوقع رفتار سے کم ہیں — ہر آرڈر اہم ہے، ہمیں یقین ہے آپ پہنچ جائیں گے! 💪🚀",
      },
    },
    on: {
      grad: "from-amber-400 via-yellow-500 to-amber-500",
      badge: { ar: "قريب جداً من الهدف 🎯", en: "Almost there 🎯", ur: "ہدف کے قریب 🎯" },
      msg: {
        ar: "أحسنت! أنت قريب جداً من هدف هذه الفترة — تبقّى القليل فقط، واصل تألقك! 🌟",
        en: "Great! You're very close to this period's target — just a little more, keep it up! 🌟",
        ur: "شاندار! آپ ہدف کے بہت قریب ہیں — بس تھوڑا سا اور! 🌟",
      },
    },
    above: {
      grad: "from-emerald-400 via-green-600 to-teal-600",
      badge: { ar: "على المسار الصحيح ✅", en: "On track ✅", ur: "درست راستے پر ✅" },
      msg: {
        ar: "أداؤك ممتاز! تجاوزت المطلوب لهذه الفترة وأنت على المسار الصحيح لتحقيق هدفك الشهري — واصل تألقك! 🌟",
        en: "Great pace! You've beaten this period's requirement and you're on track for the monthly target — keep it up! 🌟",
        ur: "زبردست رفتار! آپ اس مدت کا تقاضا عبور کر چکے ہیں اور ماہانہ ہدف کی طرف درست راستے پر ہیں! 🌟",
      },
    },
    monthly: {
      grad: "from-[#e0a458] via-[#b9752f] to-[#7c4a22]",
      badge: { ar: "حقّقت الهدف الشهري 🥉🏆", en: "Monthly target achieved 🥉🏆", ur: "ماہانہ ہدف مکمل 🥉🏆" },
      msg: {
        ar: "إنجاز استثنائي! حقّقت هدفك الشهري كاملاً بل وتجاوزته — وصلت للمستوى البرونزي، شكراً لتميّزك وجهدك الكبير، أنت مصدر فخر لنا! 🥉🎉👏",
        en: "Exceptional! You've reached and surpassed your full monthly target — bronze level, thank you for your outstanding effort! 🥉🎉👏",
        ur: "شاندار! آپ نے مکمل ماہانہ ہدف عبور کر لیا — کانسی سطح، آپ کی محنت کا شکریہ! 🥉🎉👏",
      },
    },
  };
  const tier = (target > 0 && reportDays > 0)
    ? (totalCompleted >= target ? "monthly" : gap >= 0 ? "above" : gap >= -5 ? "on" : "under")
    : null;
  const bannerGrad = tier ? TGT[tier].grad : "from-amber-400 via-orange-500 to-rose-500";
  const bannerBadge = tier ? (TGT[tier].badge[lang] || TGT[tier].badge.ar) : t("perf.rider.congrats");
  const bannerMsg = tier ? (TGT[tier].msg[lang] || TGT[tier].msg.ar) : null;

  const riderBlock = rider.matched ? (
    <div>
      {celebration && <CelebrationOverlay type={celebration} lang={lang} onClose={() => setCelebration(null)} />}
      <div className="mb-2 flex items-center justify-between gap-2 flex-wrap">
        <p className="font-heading font-bold text-slate-700 text-sm">{t("perf.rider.title")}</p>
        {latest.date && <span className="text-[11px] text-slate-400">{t("perf.rider.lastDay")}: <span className="font-mono-nums" dir="ltr">{latest.date}</span></span>}
      </div>
      <p className="mb-2 text-[11px] text-slate-400">{t("perf.rider.tapHint")}</p>
      <div data-testid="perf-total-completed" className={`relative mb-3 overflow-hidden rounded-3xl p-5 text-white shadow-lg bg-gradient-to-br ${bannerGrad}`}>
        <Sparkles className="pointer-events-none absolute -top-3 left-8 h-16 w-16 rotate-12 text-white/15" />
        <Star className="pointer-events-none absolute top-10 left-28 h-7 w-7 text-white/20 fill-white/20" />
        <PartyPopper className="pointer-events-none absolute bottom-1 left-3 h-11 w-11 -rotate-12 text-white/15" />
        <div className="pointer-events-none absolute -bottom-7 -left-7 h-24 w-24 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -top-9 right-20 h-20 w-20 rounded-full bg-white/10" />
        <div className="relative flex items-center justify-between gap-3">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/25 px-2.5 py-1 text-[11px] font-bold backdrop-blur-sm">
              <PartyPopper className="h-3.5 w-3.5" /> {bannerBadge}
            </span>
            <p className="mt-2 text-xs text-white/90">{t("perf.rider.totalCompleted")}</p>
            <p className="font-heading text-4xl font-extrabold font-mono-nums leading-none mt-1 drop-shadow-md" dir="ltr">{totalCompleted.toLocaleString("en-US")}</p>
            {target > 0 && (
              <div data-testid="perf-target-line" className="mt-1.5 space-y-0.5">
                <p className="flex items-center gap-1 text-[11px] font-semibold text-white/95">
                  <TrendingUp className="h-3.5 w-3.5" /> {lang === "en" ? "Monthly target" : lang === "ur" ? "ماہانہ ہدف" : "الهدف الشهري"}: <span className="font-mono-nums" dir="ltr">{Number(target).toLocaleString("en-US")}</span>
                </p>
                {expectedTarget > 0 && (
                  <p data-testid="perf-target-expected" className="text-[11px] text-white/90">
                    {lang === "en" ? `Required in ${reportDays} days` : lang === "ur" ? `${reportDays} دن میں مطلوب` : `المطلوب خلال ${reportDays} يوم`}: <b className="font-mono-nums" dir="ltr">{expectedTarget.toLocaleString("en-US")}</b>
                  </p>
                )}
              </div>
            )}
            <p className="mt-1.5 flex items-center gap-1 text-[11px] text-white/90"><Sparkles className="h-3.5 w-3.5" /> {t("perf.rider.duringDays", { n: rider.days ?? 0 })}</p>
            {bannerMsg && <p data-testid="perf-target-msg" className="mt-2 max-w-md text-[12px] leading-relaxed font-semibold text-white drop-shadow-sm">{bannerMsg}</p>}
          </div>
          <span className="relative grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-white/25 backdrop-blur-sm animate-pulse">
            <Trophy className="h-8 w-8 text-white drop-shadow" />
          </span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Stat icon={Package} label={t("perf.stat.completedLast")} value={latest.completed} tone="green" onClick={() => openHist("completed")} hint={t("perf.stat.totalPrefix", { n: rider.summary?.total_completed ?? 0 })} />
        <Stat icon={Clock} label={t("perf.stat.hoursLast")} value={hrs(lh)} tone={typeof lh === "number" && lh < HMIN ? "rose" : "amber"} onClick={() => openHist("actual_hours")} hint={typeof lh === "number" && lh < HMIN ? t("perf.stat.lessThanHours", { n: HMIN }) : null} />
        <Stat icon={Package} label={t("perf.stat.rejectedLast")} value={lr} tone={(lr || 0) >= RMAX ? "rose" : "blue"} onClick={() => openHist("rejected")} hint={(lr || 0) >= RMAX ? t("perf.stat.orMore", { n: RMAX }) : null} />
        <Stat icon={CheckCircle2} label={t("perf.stat.acceptance")} value={pct(rider.summary?.avg_acceptance)} suffix="%" tone="blue" onClick={() => openHist("acceptance_rate")} />
        <Stat icon={TrendingUp} label={t("perf.stat.reportDays")} value={rider.days} tone="amber" onClick={() => openHist("date")} />
        <Stat icon={UserX} label={t("perf.stat.noShows")} value={rider.summary?.total_no_shows} tone="rose" onClick={() => openHist("no_shows")} />
      </div>
      {rider.trend?.length > 1 && (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3">
          <p className="mb-1 text-xs font-semibold text-slate-500">{t("perf.trend.completedTitle")}</p>
          <TrendChart data={rider.trend} dataKey="completed" name={t("perf.trend.completedName")} color="#2563eb" />
        </div>
      )}
      <DayHistoryModal open={!!hist} onClose={() => setHist(null)} days={daily} highlight={hist?.key} minHours={HMIN} maxRejected={RMAX} />
    </div>
  ) : (
    <EmptyBox icon={Package} title={t("perf.rider.noMatchTitle")} hint={t("perf.rider.noMatchHint")} />
  );

  const historyBlock = evals.length > 0 ? (
    <div className="space-y-3" data-testid="monthly-eval-history">
      <div className="flex items-center gap-2">
        <History className="h-4 w-4 text-blue-500" />
        <p className="font-heading font-bold text-slate-700 text-sm">{t("perf.eval.title")}</p>
        <Badge variant="outline" className="rounded-full text-[11px]">{t("perf.eval.monthsCount", { n: evals.length })}</Badge>
      </div>
      {evals.length > 1 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <p className="mb-1 text-xs font-semibold text-slate-500">{t("perf.eval.trendTitle")}</p>
          <div className="h-44 w-full" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={[...evals].reverse().map((ev) => ({ date: ev.month, score: ev.score }))}
                margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={28} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Line type="monotone" dataKey="score" name={t("perf.eval.scoreName")} stroke="#2563eb" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      {evals.map((ev) => {
        const g = gradeFromScore(ev.score);
        return (
          <div key={ev.id} data-testid={`eval-item-${ev.id}`} className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg font-heading text-base font-extrabold ${GRADE_CLS[g] || "bg-slate-100 text-slate-400"}`}>{g || "—"}</span>
                <span className="font-mono-nums font-semibold text-slate-700">{ev.month}</span>
                <Stars value={ev.score} />
                <span className="text-sm text-slate-500 font-mono-nums">{ev.score}/5</span>
              </div>
              {editable && (
                <button data-testid={`eval-delete-${ev.id}`} onClick={() => onDelete(ev.id)}
                  className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
            {ev.notes && <p className="mt-1 text-sm text-slate-600">{ev.notes}</p>}
            <p className="mt-1 text-xs text-slate-400">{t("perf.eval.by", { n: ev.actor_name || "—" })}</p>
          </div>
        );
      })}
    </div>
  ) : (
    <EmptyBox icon={History} title={t("perf.eval.emptyTitle")} hint={t("perf.eval.emptyHint")} />
  );

  const pd = data?.payable_distance;
  const km = (v) => Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 1 });
  const distanceCard = pd && (
    <div data-testid="perf-distance-card" className="rounded-3xl bg-gradient-to-br from-indigo-600 to-sky-600 p-4 text-white shadow-lg">
      <div className="flex items-center justify-between gap-2">
        <p className="font-heading font-bold text-sm flex items-center gap-1.5"><Route className="h-4 w-4" /> {t("perf.dist.title")}</p>
        <span className="text-[11px] text-white/80 font-mono-nums" dir="ltr">{pd.month}</span>
      </div>
      {pd.empty ? <p className="mt-3 text-sm text-white/85" data-testid="perf-distance-empty">{t("perf.dist.empty")}</p> : (
        <div className="mt-3 grid grid-cols-2 gap-2 text-center">
          <div className="rounded-2xl bg-white/15 p-2"><p className="text-[11px] text-white/80">{t("perf.dist.payable")}</p><p className="font-heading text-xl font-extrabold font-mono-nums" data-testid="perf-distance-payable">{km(pd.payable_km)}</p><p className="text-[10px] text-white/70">{t("perf.stat.kmUnit")}</p></div>
          <div className="rounded-2xl bg-white/15 p-2"><p className="text-[11px] text-white/80">{t("perf.dist.days")}</p><p className="font-heading text-xl font-extrabold font-mono-nums">{pd.days}</p><p className="text-[10px] text-white/70">{t("perf.dist.deliveries", { n: pd.completed_deliveries ?? 0 })}</p></div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {distanceCard}
      {showAdvances ? (
        <div className="grid gap-4 lg:grid-cols-3 items-start">
          <div className="lg:col-span-1">{advancesCard}</div>
          <div className="lg:col-span-2" data-testid="perf-history-col">{historyBlock}</div>
        </div>
      ) : historyBlock}
      {riderBlock}
    </div>
  );
};

export const AttendanceTab = ({ data, loading }) => {
  const { t } = useLang();
  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const rider = data?.rider || {};
  if (!rider.matched) {
    return <EmptyBox icon={CalendarDays} title={t("perf.att.noDataTitle")} hint={t("perf.att.noDataHint")} />;
  }
  const at = rider.attendance || {};
  const attTrend = (rider.trend || []).map((p) => ({ ...p, attendance_rate: pct(p.attendance_rate) }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={ClipboardCheck} label={t("perf.att.avgAttendance")} value={pct(at.avg_attendance)} suffix="%" tone="green" />
        <Stat icon={CalendarDays} label={t("perf.att.workingDays")} value={at.working_days} tone="blue" />
        <Stat icon={Clock} label={t("perf.att.actualHours")} value={hrs(at.actual_hours)} tone="amber" />
        <Stat icon={UserX} label={t("perf.att.noShows")} value={at.no_shows} tone="rose" />
      </div>
      {attTrend.length > 1 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <p className="mb-1 text-xs font-semibold text-slate-500">{t("perf.att.trendTitle")}</p>
          <TrendChart data={attTrend} dataKey="attendance_rate" name={t("perf.att.trendName")} color="#16a34a" />
        </div>
      )}
      <p className="text-xs text-slate-400">{t("perf.att.period", { a: rider.date_range?.[0], b: rider.date_range?.[1] })}</p>
    </div>
  );
};

const ACTION_META = {
  create: { icon: Plus, tone: "text-emerald-600 bg-emerald-50" },
  update: { icon: Pencil, tone: "text-blue-600 bg-blue-50" },
  photo: { icon: Camera, tone: "text-violet-600 bg-violet-50" },
  evaluation: { icon: ClipboardCheck, tone: "text-amber-600 bg-amber-50" },
  document: { icon: FilePlus2, tone: "text-sky-600 bg-sky-50" },
};

export const ActivityTab = ({ data, loading, createdAt }) => {
  const { t } = useLang();
  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const logs = data?.logs || [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-2.5">
        <span className="text-sm text-slate-500">{t("perf.activity.addedDate")}</span>
        <span className="font-mono-nums text-sm font-semibold text-slate-700">{createdAt ? String(createdAt).slice(0, 10) : "—"}</span>
      </div>
      {logs.length === 0 ? (
        <EmptyBox icon={History} title={t("perf.activity.emptyTitle")} hint={t("perf.activity.emptyHint")} />
      ) : (
        <ol className="relative space-y-3 pr-4" data-testid="activity-timeline">
          {logs.map((l) => {
            const meta = ACTION_META[l.action] || { icon: Activity, tone: "text-slate-600 bg-slate-100" };
            const Icon = meta.icon;
            const actionLabel = t(`perf.action.${l.action}`);
            return (
              <li key={l.id} data-testid={`activity-item-${l.id}`} className="flex items-start gap-3">
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${meta.tone}`}><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white p-2.5">
                  <p className="text-sm text-slate-700">{l.detail || actionLabel}</p>
                  <p className="mt-0.5 text-xs text-slate-400">
                    {l.actor_name || "—"} · <span className="font-mono-nums">{String(l.at || "").slice(0, 16).replace("T", " ")}</span>
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};
